import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link as LinkIcon, Check } from '@phosphor-icons/react';

/**
 * Jira-style ticket permalink chip.
 *
 * Two affordances bundled together — same pattern as Jira / Linear:
 *   1. The task key (e.g. `WORK01-4`) is an anchor that NAVIGATES to
 *      `/browse/<KEY>`. From the slide-over this opens the full task page;
 *      from anywhere else it deep-links straight to it. Right-click + "Copy
 *      link" works because it's a real <a href>.
 *   2. A tiny icon button NEXT to the key explicitly copies that URL to
 *      the clipboard and pulses a green tick + "Copied" label for ~1.5s.
 *
 * The two are intentionally separate so a normal click never "steals" the
 * navigation — the user said the previous "click = copy" was confusing.
 */
export default function CopyTaskLink({ taskKey, className = '' }) {
  const [copied, setCopied] = useState(false);
  if (!taskKey) return null;
  const path = `/browse/${taskKey}`;
  const fullUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}${path}`;

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch {
      // Older browsers / non-secure contexts: fall back to execCommand.
      try {
        const ta = document.createElement('textarea');
        ta.value = fullUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { /* clipboard simply isn't available */ }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <RouterLink
        to={path}
        title={`Open ${taskKey}`}
        data-testid="task-key-link"
        className="text-[12px] font-mono text-zinc-700 font-semibold hover:text-indigo-700 hover:underline"
      >
        {taskKey}
      </RouterLink>
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? 'Copied!' : `Copy link: ${fullUrl}`}
        data-testid="copy-task-link"
        className="inline-flex items-center gap-1 px-1 py-0.5 rounded hover:bg-indigo-50 transition-colors"
      >
        {copied ? (
          <>
            <Check size={12} weight="bold" className="text-emerald-600" />
            <span className="text-[10px] text-emerald-700 font-medium">Copied</span>
          </>
        ) : (
          <LinkIcon size={12} className="text-zinc-400 hover:text-indigo-600" />
        )}
      </button>
    </span>
  );
}
